import type { TypedSupabaseClient } from '../client';

// ------------------------------------------------------------------
// Legacy bare-filename → full-URL resolver (shared)
//
// Some image columns (notably `articles.image_url` and `articles.images[]`)
// historically store bare paths like `234aaaf9-….png` instead of full
// Supabase public URLs. Rendered as-is in `<img src>`, the browser resolves
// them relative to the current page and 404s. This helper turns those bare
// paths into the canonical public URL for the `erp-images` bucket, scoped
// under `<tenant_id>/<folder>/<file>` to match how uploads land.
//
// Already-absolute values (http(s), data:, blob:) pass through untouched.
// Empty / non-string values resolve to null so callers show a placeholder
// instead of a broken image.
//
// This is the single source of truth for path resolution — both the article
// repository (erp-web / erp-pro dashboards) and the storefront repository
// import it so every surface resolves images identically. It runs on BOTH
// sides: on read (mapArticle) to fix legacy rows, and on write (article
// create/update) so we never *persist* a bare path in the first place.
// ------------------------------------------------------------------

/**
 * True when `value` is a non-empty string that is NOT already an absolute
 * reference (http(s), data:, blob:). These are the "bare" storage paths/
 * filenames that must be expanded to a full public URL before being stored
 * or rendered. Used to avoid resolving the tenant id when every incoming
 * value is already absolute (the common form-upload path).
 */
export function isBareStoragePath(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return !/^(https?:|data:|blob:)/i.test(trimmed);
}

export function resolveStorageImageUrl(
  supabase: TypedSupabaseClient,
  value: string | null | undefined,
  opts: { tenantId: string | null; folder: string; bucket?: string },
): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!isBareStoragePath(trimmed)) return trimmed;
  const bucket = opts.bucket ?? 'erp-images';
  const folder = opts.folder.replace(/^\/+|\/+$/g, '');
  const file = trimmed.replace(/^\/+/, '');
  const path = opts.tenantId
    ? `${opts.tenantId}/${folder}/${file}`
    : `${folder}/${file}`;
  const storage = (supabase as unknown as {
    storage: { from: (b: string) => { getPublicUrl: (p: string) => { data: { publicUrl: string } } } };
  }).storage;
  return storage.from(bucket).getPublicUrl(path).data.publicUrl ?? null;
}
